import { useCallback, useState } from "react";

interface Options {
  /**
   * Allow blank string `""` to pass validations. Defaults to `true`.
   */
  allowBlank?: boolean;
  /**
   * Default value.
   */
  default?: string | number;
  /**
   * Maximum number of decimal place allowed for input.
   */
  maxDecimalPlace?: number;
  /**
   * Minimum number.
   */
  min?: number;
}

type Return = [number, string, (value: string | number) => void];

function parseNumber(value?: string | number): number {
  if (value == null || value === "") return 0;
  return typeof value === "string" ? parseFloat(value) : value;
}

function validate(
  stringValue: string,
  numberValue: number,
  opts?: Options
): boolean {
  // Allow empty
  if (opts?.maxDecimalPlace) {
    const decimalPlaceRegex = new RegExp(
      `^-?\\d+(?:\\.\\d{0,${opts.maxDecimalPlace}})?$`
    );
    if (!decimalPlaceRegex.test(stringValue)) return false;
  }

  if (opts?.min != null) {
    if (numberValue < opts.min) return false;
  }

  return true;
}

/**
 * State to easily manage value for controlled `<input>` type number. First
 * return is the value as number, useful for doing number calculations. Second
 * return is the value as string, useful for passing to value prop of `<input>`.
 * Third return is the function to update the value.
 */
export default function useNumberInputState(opts?: Options): Return {
  const [number, setNumber] = useState<number>(parseNumber(opts?.default));
  const [string, setString] = useState<string>(opts?.default?.toString() ?? "");

  const allowBlank = opts?.allowBlank ?? true;
  const maxDecimalPlace = opts?.maxDecimalPlace;
  const min = opts?.min;

  const setValue = useCallback(
    (value: string | number) => {
      const stringValue = value.toString();
      const numberValue = parseNumber(value);

      const valid =
        allowBlank && stringValue === ""
          ? true
          : validate(stringValue, numberValue, { maxDecimalPlace, min });

      if (valid) {
        setNumber(numberValue);
        setString(stringValue);
      }
    },
    [allowBlank, maxDecimalPlace, min]
  );

  return [number, string, setValue];
}
